import { useState, useMemo, useEffect, useCallback, useRef } from 'react';

import { useParams, useNavigate } from 'react-router-dom';

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';
import { useErrorStore } from '#lib/stores/errorStore';
import { useOrgMembersBasic } from '#orgFeature/models/org/queries';
import { useUserSettings } from '#settingsFeature/models/userSettings/queries';

import {
	getCompanyDetail,
	updateCompany,
	updateDeal,
	updateContact,
	createContact,
	deleteContact,
	deleteDeal,
	transitionStage,
	aircallDial,
} from '../../api/crmApi';
import { useCallingMode, useAircallEverywhere } from '../../components/aircallEverywhere/useAircallEverywhere';
import { useSoftDeleteCompany } from '../../models/companies/mutations';
import { useVisibleFieldDefs } from '../../models/customFields/queries';
import { useCreateDeal } from '../../models/deals/mutations';
import { useIntegrationsList } from '../../models/integrations/queries';
import { usePipelines } from '../../models/pipelines/queries';
import { useProductsList, useBundlesList } from '../../models/products/queries';
import { useTags } from '../../models/tags/queries';
import { useAircallEverywhereStore } from '../../stores/aircallEverywhereStore';

export default function useCompanyPageData() {
	const { companyId } = useParams();
	const navigate = useNavigate();
	const showError = useErrorStore((s) => s.showError);
	const showSuccess = useErrorStore((s) => s.showSuccess);
	const qc = useQueryClient();

	// Queries
	const { data, isLoading } = useQuery({
		queryKey: ['crm', 'company', companyId],
		queryFn: () => getCompanyDetail(companyId),
		staleTime: STALE_TIMES.FREQUENT,
		enabled: Boolean(companyId),
	});

	// Fold the detail payload's tags onto the company object so `company.tags` is available
	// to the tags card (and any other consumer) without threading a separate prop.
	const company = useMemo(
		() => (data?.company ? { ...data.company, tags: data.tags ?? [] } : data?.company),
		[data?.company, data?.tags],
	);
	const deals = useMemo(() => data?.deals ?? [], [data?.deals]);
	const contacts = data?.contacts ?? [];
	const { data: tagsData } = useTags();
	const allTags = useMemo(() => tagsData?.tags ?? [], [tagsData?.tags]);

	const { data: membersData } = useOrgMembersBasic();
	const members = membersData?.members ?? [];
	const { data: productsData } = useProductsList();
	const { data: bundlesData } = useBundlesList();
	const allProducts = productsData?.products ?? [];
	const allBundles = bundlesData?.bundles ?? [];
	const { data: pipelines = [] } = usePipelines();
	const defaultPipeline = pipelines.find((p) => p.isDefault) ?? pipelines[0];
	const defaultStages = defaultPipeline?.stages
		? [...defaultPipeline.stages].sort((a, b) => a.stageOrder - b.stageOrder)
		: [];
	const createDealMutation = useCreateDeal();
	const softDeleteMutation = useSoftDeleteCompany();
	const { data: userSettingsData } = useUserSettings();
	const aiNextActionEnabled = userSettingsData?.userSettings?.aiNextActionEnabled !== false;
	const { data: companyFieldDefs = [] } = useVisibleFieldDefs('company');

	// Derived data
	const stagesByPipeline = useMemo(() => {
		const map = {};
		for (const d of deals) {
			if (d.pipelineStages && !map[d.pipelineId]) {
				const sorted = [...d.pipelineStages].sort((a, b) => a.stageOrder - b.stageOrder);
				sorted.forEach((s, i) => {
					s._colorIndex = i;
				});
				map[d.pipelineId] = sorted;
			}
		}
		return map;
	}, [deals]);

	const { data: integrations = [] } = useIntegrationsList();
	const aircallIntegration = integrations.find((i) => i.slug === 'phone_aircall');
	const isAircallConnected = aircallIntegration?.isConnected ?? false;

	const openDeals = deals.filter((d) => d.status === 'open');
	const pipelineValue = openDeals.reduce((sum, d) => sum + Number(d.dealValue ?? 0), 0);

	// Active deal
	const [activeDealId, setActiveDealId] = useState(null);
	const hasAutoSelected = useRef(false);
	useEffect(() => {
		if (!hasAutoSelected.current && deals.length > 0 && !activeDealId) {
			const firstOpen = deals.find((d) => d.status === 'open');
			setActiveDealId(firstOpen?.id ?? deals[0].id);
			hasAutoSelected.current = true;
		}
	}, [deals, activeDealId]);

	const activeDeal = deals.find((d) => d.id === activeDealId);
	const ownerMember = activeDeal ? members.find((m) => m.userId === activeDeal.users?.[0]?.id) : null;

	// Invalidation
	const invalidate = useCallback(() => {
		qc.invalidateQueries({ queryKey: ['crm', 'company', companyId] });
		qc.invalidateQueries({ queryKey: ['crm', 'deals'] });
		qc.invalidateQueries({ queryKey: ['crm', 'activities'] });
	}, [qc, companyId]);

	// Mutations
	const companyMutation = useMutation({
		mutationFn: (payload) => updateCompany(companyId, payload),
		onSuccess: invalidate,
		onError: (err) => showError({ error: err, title: 'Speichern fehlgeschlagen' }),
	});

	const callingMode = useCallingMode();
	const { dial: dialEverywhere } = useAircallEverywhere();
	const setWidgetVisible = useAircallEverywhereStore((s) => s.setWidgetVisible);

	const [dialLoading, setDialLoading] = useState(false);
	const handleAircallDial = async (phoneNumber) => {
		if (!phoneNumber || dialLoading) return;
		setDialLoading(true);
		try {
			if (callingMode === 'everywhere') {
				setWidgetVisible(true);
				await dialEverywhere(phoneNumber);
			} else {
				await aircallDial(phoneNumber);
			}
		} catch (err) {
			if (callingMode === 'everywhere') {
				showError({ message: err.message, title: 'Anruf konnte nicht gestartet werden' });
			} else {
				showError({ error: err, title: 'Anruf konnte nicht gestartet werden' });
			}
		} finally {
			setDialLoading(false);
		}
	};

	const handleCreateDeal = async (payload) => {
		try {
			await createDealMutation.mutateAsync(payload);
			invalidate();
		} catch (err) {
			showError({ error: err, title: 'Deal konnte nicht erstellt werden' });
			throw err;
		}
	};

	const handleDealUpdate = async (dealId, patch) => {
		try {
			await updateDeal(dealId, patch);
			invalidate();
		} catch (err) {
			showError({ error: err, title: 'Deal-Update fehlgeschlagen' });
		}
	};

	// Stage transition + lost-reason capture
	// Moving a deal into a lost-type stage opens a prompt for an (optional) loss
	// reason before persisting; any other target transitions immediately.
	const [lossPrompt, setLossPrompt] = useState(null);
	const [lossSubmitting, setLossSubmitting] = useState(false);

	const persistTransition = async (dealId, stageId, lossReason = null, lossReasonNote = null) => {
		await transitionStage(dealId, {
			stageId,
			...(lossReason !== null ? { lossReason } : {}),
			...(lossReasonNote !== null ? { lossReasonNote } : {}),
		});
		invalidate();
	};

	const handleStageTransition = async (dealId, stageId) => {
		const deal = deals.find((d) => d.id === dealId);
		const targetStage = (stagesByPipeline[deal?.pipelineId] ?? []).find((s) => s.id === stageId);

		if (targetStage?.stageType === 'lost') {
			setLossPrompt({ dealId, stageId });
			return;
		}

		try {
			await persistTransition(dealId, stageId);
		} catch (err) {
			showError({ error: err, title: 'Stage konnte nicht gewechselt werden' });
		}
	};

	const confirmLossTransition = async ({ lossReason, lossReasonNote }) => {
		if (!lossPrompt) return;
		setLossSubmitting(true);
		try {
			await persistTransition(lossPrompt.dealId, lossPrompt.stageId, lossReason, lossReasonNote);
			setLossPrompt(null);
		} catch (err) {
			showError({ error: err, title: 'Stage konnte nicht gewechselt werden' });
		} finally {
			setLossSubmitting(false);
		}
	};

	const handleDealDelete = async (dealId) => {
		try {
			await deleteDeal(dealId);
			invalidate();
			if (activeDealId === dealId) setActiveDealId(null);
		} catch (err) {
			showError({ error: err, title: 'Deal konnte nicht gelöscht werden' });
		}
	};

	const handleCreateContact = async (payload) => {
		try {
			const result = await createContact(payload);
			invalidate();
			return result;
		} catch (err) {
			showError({ error: err, title: 'Kontakt konnte nicht erstellt werden' });
			throw err;
		}
	};

	const handleContactUpdate = async (contactId, patch) => {
		try {
			await updateContact(contactId, patch);
			invalidate();
		} catch (err) {
			showError({ error: err, title: 'Kontakt-Update fehlgeschlagen' });
		}
	};

	const handleContactDelete = async (contactId) => {
		try {
			await deleteContact(contactId);
			invalidate();
		} catch (err) {
			showError({ error: err, title: 'Kontakt konnte nicht entfernt werden' });
		}
	};

	const handleCompanyDelete = async () => {
		try {
			await softDeleteMutation.mutateAsync(companyId);
			showSuccess({
				title: 'In den Papierkorb verschoben',
				message: `${company.companyName} wurde in den Papierkorb verschoben.`,
				autoClose: true,
			});
			navigate('/crm/pipeline');
		} catch (err) {
			showError({ error: err, title: 'Löschen fehlgeschlagen' });
		}
	};

	return {
		companyId,
		company,
		deals,
		contacts,
		allTags,
		isLoading,
		members,
		allProducts,
		allBundles,
		defaultPipeline,
		defaultStages,
		stagesByPipeline,
		isAircallConnected,
		dialLoading,
		openDeals,
		pipelineValue,
		activeDealId,
		setActiveDealId,
		activeDeal,
		ownerMember,
		aiNextActionEnabled,
		companyFieldDefs,
		companyMutation,
		softDeleteMutation,
		handleAircallDial,
		handleCreateDeal,
		handleDealUpdate,
		handleStageTransition,
		handleDealDelete,
		handleCreateContact,
		handleContactUpdate,
		handleContactDelete,
		handleCompanyDelete,
		lossPrompt,
		lossSubmitting,
		confirmLossTransition,
		setLossPrompt,
	};
}
